// src/pages/Admin/SharePointLibrariesPage.tsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Star, StarBorder, Block } from '@mui/icons-material';
import {
  getAllSharePointLibraries,
  createSharePointLibrary,
  updateSharePointLibrary,
  setDefaultSharePointLibrary,
  deleteSharePointLibrary,
  SharePointLibrary,
  SharePointLibraryApiError,
  CreateLibraryRequest,
} from '../../services/sharePointLibraryApi';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../services/authApi';

const emptyForm: CreateLibraryRequest & { isActive?: boolean } = {
  displayName: '',
  libraryName: '',
  siteId: '',
  webUrl: '',
  isDefault: false,
  isActive: true,
};

export function SharePointLibrariesPage() {
  const { user } = useAuth();
  const [libraries, setLibraries] = useState<SharePointLibrary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SharePointLibrary | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const canEditSettings = user && hasRole(user, 'System Admin');

  const loadLibraries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAllSharePointLibraries(true);
      setLibraries(response.libraries || []);
    } catch (err) {
      const apiError = err as SharePointLibraryApiError;
      setError(apiError.message || 'Failed to load SharePoint destinations');
      console.error('Error loading SharePoint destinations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLibraries();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...emptyForm });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (lib: SharePointLibrary) => {
    setEditing(lib);
    setFormData({
      displayName: lib.displayName || lib.libraryName,
      libraryName: lib.libraryName,
      siteId: lib.siteId,
      webUrl: lib.webUrl || '',
      isDefault: lib.isDefault,
      isActive: lib.isActive,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!formData.libraryName || formData.libraryName.trim() === '') {
      errors.libraryName = 'Library name is required';
    }
    if (!formData.siteId || formData.siteId.trim() === '') {
      errors.siteId = 'Site ID is required';
    }
    if (formData.webUrl && formData.webUrl.trim() !== '') {
      try {
        new URL(formData.webUrl);
      } catch {
        errors.webUrl = 'Invalid URL format';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      if (editing) {
        await updateSharePointLibrary(editing.libraryId, {
          displayName: formData.displayName || formData.libraryName,
          libraryName: formData.libraryName,
          siteId: formData.siteId,
          webUrl: formData.webUrl || undefined,
          isActive: formData.isActive,
          isDefault: formData.isDefault === true ? true : undefined,
        });
        setSuccessMessage('SharePoint destination updated');
      } else {
        await createSharePointLibrary({
          displayName: formData.displayName || formData.libraryName,
          libraryName: formData.libraryName,
          siteId: formData.siteId,
          webUrl: formData.webUrl || undefined,
          isDefault: formData.isDefault === true,
        });
        setSuccessMessage('SharePoint destination created');
      }
      setDialogOpen(false);
      await loadLibraries();
    } catch (err) {
      const apiError = err as SharePointLibraryApiError;
      setError(apiError.message || 'Failed to save SharePoint destination');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = async (lib: SharePointLibrary) => {
    setError(null);
    setSuccessMessage(null);
    try {
      await setDefaultSharePointLibrary(lib.libraryId);
      setSuccessMessage(`"${lib.displayName || lib.libraryName}" is now the default destination`);
      await loadLibraries();
    } catch (err) {
      const apiError = err as SharePointLibraryApiError;
      setError(apiError.message || 'Failed to set default destination');
    }
  };

  const handleDeactivate = async (lib: SharePointLibrary) => {
    if (lib.isDefault) {
      setError('Set another destination as default before deactivating this one.');
      return;
    }
    setError(null);
    try {
      await deleteSharePointLibrary(lib.libraryId);
      setSuccessMessage(`"${lib.displayName || lib.libraryName}" deactivated`);
      await loadLibraries();
    } catch (err) {
      const apiError = err as SharePointLibraryApiError;
      setError(apiError.message || 'Failed to deactivate destination');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'white' }}>
          SharePoint Destinations
        </Typography>
        {canEditSettings && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Add Destination
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
            Configure one or more SharePoint sites/libraries. Exactly one active destination is the
            system default. Project Template and Change Order actions may override the default for
            new documents.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Library Name</TableCell>
                  <TableCell>Site ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {libraries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">
                        No SharePoint destinations configured yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {libraries.map((lib) => (
                  <TableRow key={lib.libraryId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {lib.displayName || lib.libraryName}
                        {lib.isDefault && (
                          <Chip size="small" color="primary" label="Default" icon={<Star />} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{lib.libraryName}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {lib.siteId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={lib.isActive ? 'Active' : 'Inactive'}
                        color={lib.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canEditSettings && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(lib)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!lib.isDefault && lib.isActive && (
                            <Tooltip title="Set as default">
                              <IconButton size="small" onClick={() => handleSetDefault(lib)}>
                                <StarBorder fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {lib.isActive && !lib.isDefault && (
                            <Tooltip title="Deactivate">
                              <IconButton size="small" onClick={() => handleDeactivate(lib)}>
                                <Block fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => !isSaving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit SharePoint Destination' : 'Add SharePoint Destination'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Display Name"
              value={formData.displayName || ''}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              helperText="Friendly name shown to planners"
              fullWidth
              disabled={isSaving}
            />
            <TextField
              label="Library Name"
              value={formData.libraryName}
              onChange={(e) => {
                setFormData({ ...formData, libraryName: e.target.value });
                setFormErrors({ ...formErrors, libraryName: '' });
              }}
              error={!!formErrors.libraryName}
              helperText={formErrors.libraryName || 'SharePoint document library (drive) name'}
              required
              fullWidth
              disabled={isSaving}
            />
            <TextField
              label="Site ID"
              value={formData.siteId}
              onChange={(e) => {
                setFormData({ ...formData, siteId: e.target.value });
                setFormErrors({ ...formErrors, siteId: '' });
              }}
              error={!!formErrors.siteId}
              helperText={formErrors.siteId || 'SharePoint site ID (GUID)'}
              required
              fullWidth
              disabled={isSaving}
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
            />
            <TextField
              label="Web URL"
              value={formData.webUrl || ''}
              onChange={(e) => {
                setFormData({ ...formData, webUrl: e.target.value });
                setFormErrors({ ...formErrors, webUrl: '' });
              }}
              error={!!formErrors.webUrl}
              helperText={formErrors.webUrl || 'Optional SharePoint site URL'}
              fullWidth
              disabled={isSaving}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  disabled={isSaving || (!!editing && editing.isDefault)}
                />
              }
              label="Set as system default"
            />
            {editing && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive !== false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    disabled={isSaving || editing.isDefault}
                  />
                }
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
